/**
 * DDX-Plus KB ingest pipeline — job / staging types (JSDoc + runtime constants).
 * @see docs/DDX-PLUS-KB-INGESTION-PIPELINE.md
 */

/** @typedef {'uploaded'|'processing'|'ai_reviewing'|'ai_review_failed'|'ready_for_review'|'promoted'|'failed'|'cancelled'} DdxIngestJobStatus */

/**
 * @typedef {Object} DdxIngestJob
 * @property {string} id
 * @property {DdxIngestJobStatus} status
 * @property {string} createdByUid
 * @property {string} [createdAt] - ISO
 * @property {string} [sourceFilename]
 * @property {string} [sourceStoragePath] - e.g. ddxIngest/{uid}/{jobId}/original.tsv
 * @property {number} [rowCountRaw]
 * @property {number} [rowCountStaged]
 * @property {string[]} [errors]
 * @property {string[]} [warnings]
 * @property {Object} [summary] - e.g. duplicatesMerged, conflictsFlagged
 * @property {Object} [columnMap] - index → field key for parser
 * @property {string} [delimiter] - default tab
 * @property {Object} [aiReviewJobSummary] - denormalized AI finding counts (server)
 */

/**
 * @typedef {Object} DdxIngestStagedRow
 * @property {string} jobId
 * @property {string} stableKey - slug, used in doc id
 * @property {string} displayName - chief complaint title
 * @property {Object<string, string | import('./ddxBlobRef.js').DdxBlobRef>} cleanedFields - columnKey → cleaned text or GCS blob ref
 * @property {string} [contentHash] - set by server (sha256)
 * @property {string} [dedupeGroupId]
 * @property {number} [trustTier] - default 2
 * @property {number} [sizeBytes]
 * @property {boolean} [excludeFromPromote]
 * @property {boolean} [needsManualReview]
 * @property {string[]} [parseErrors]
 * @property {string[]} [warnings]
 * @property {number} [sourceRowIndex]
 * @property {string} [aiReviewStatus]
 * @property {Object} [findingCounts]
 */

/** @type {Record<string, DdxIngestJobStatus>} */
export const DDX_INGEST_JOB_STATUS = {
  UPLOADED: 'uploaded',
  PROCESSING: 'processing',
  AI_REVIEWING: 'ai_reviewing',
  AI_REVIEW_FAILED: 'ai_review_failed',
  READY_FOR_REVIEW: 'ready_for_review',
  PROMOTED: 'promoted',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

/** Per-row AI pass status (Firestore `aiReviewStatus` on staged rows). Keep in sync with functions `AI_REVIEW_ROW_STATUS`. */
export const DDX_AI_REVIEW_ROW_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  FAILED: 'failed',
  SKIPPED: 'skipped',
};

/** Firestore collection names (keep in sync with rules + functions). */
export const DDX_INGEST_COLLECTIONS = {
  JOBS: 'ddxIngestJobs',
  STAGED_ROWS: 'ddxIngestStagedRows',
  AUDIT: 'ddxIngestAudit',
};

export const DDX_AI_FINDING_SEVERITY_ORDER = ['critical', 'high', 'medium', 'low'];

export const DDX_AI_REVIEW_FIELD_SORT_ORDER = [
  'triageRaw',
  'differentialStructuredRaw',
  'emergencyListRaw',
  'adultDdxRaw',
  'reviewerNotesRaw',
  'questionCount',
];

/**
 * Accept-fix must use reference-backed text, not batch `suggestedFix`.
 * Keep in sync with `FINDING_CATEGORIES_REQUIRING_GROUNDED_ACCEPT` in Cloud Functions `ddxIngest/constants.js`.
 */
export const DDX_AI_FINDING_CATEGORIES_REQUIRING_GROUNDED_ACCEPT = [
  'clinical_inaccuracy',
  'reference_gap',
];

/**
 * @param {string | undefined} category
 * @returns {boolean}
 */
export function ddxFindingCategoryRequiresGroundedAccept(category) {
  return (
    typeof category === 'string' &&
    DDX_AI_FINDING_CATEGORIES_REQUIRING_GROUNDED_ACCEPT.includes(category)
  );
}

/** Default spreadsheet column keys (0-based indices in v1 TSV). */
export const DDX_INGEST_DEFAULT_COLUMN_KEYS = [
  'title',
  'questionCount',
  'triageRaw',
  'differentialStructuredRaw',
  'emergencyListRaw',
  'adultDdxRaw',
  'reviewerNotesRaw',
];
